import {
  Alert,
  Button,
  Group,
  Modal,
  PasswordInput,
  Select,
  Stack,
  TextInput,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconAlertCircle } from "@/lib/icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { ApiError } from "@/lib/api/client";
import { createUser, type UserCreatePayload } from "@/lib/api/users";

import {
  type EffectiveRoleValue,
  buildRoleOptions,
  roleAndSpecialtyFrom,
} from "./roleOptions";

export function UserFormDialog({
  opened,
  onClose,
}: {
  opened: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const groupedOptions = useMemo(() => buildRoleOptions(t), [t]);

  const form = useForm({
    initialValues: {
      full_name: "",
      email: "",
      effective_role: "" as EffectiveRoleValue | "",
      initial_password: "",
    },
    validate: {
      full_name: (v) => (v.trim().length > 0 ? null : t("validation.required")),
      email: (v) =>
        v.trim().length >= 2 ? null : t("validation.required"),
      effective_role: (v) => (v ? null : t("validation.required")),
      initial_password: (v) =>
        v.length >= 6 ? null : t("users.validation.password_min"),
    },
  });

  const mutation = useMutation({
    mutationFn: createUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["users"] });
      notifications.show({ color: "green", message: t("users.created") });
      form.reset();
      onClose();
    },
  });

  const onSubmit = form.onSubmit((values) => {
    if (!values.effective_role) return;
    const { role, specialty } = roleAndSpecialtyFrom(values.effective_role);
    if (!role) return;
    const payload: UserCreatePayload = {
      email: values.email.trim().toLowerCase(),
      full_name: values.full_name.trim(),
      role,
      specialty,
      initial_password: values.initial_password,
    };
    mutation.mutate(payload);
  });

  const errorMessage = (() => {
    if (!mutation.error) return null;
    if (mutation.error instanceof ApiError) {
      if (mutation.error.code === "conflict") return t("users.errors.duplicate_email");
      return mutation.error.message;
    }
    return t("auth.network_error");
  })();

  return (
    <Modal
      opened={opened}
      onClose={() => {
        form.reset();
        onClose();
      }}
      title={t("users.create")}
      size="md"
    >
      {errorMessage && (
        <Alert color="red" icon={<IconAlertCircle size={16} />} mb="md">
          {errorMessage}
        </Alert>
      )}
      <form onSubmit={onSubmit}>
        <Stack>
          <TextInput
            label={t("users.fields.full_name")}
            placeholder={t("placeholder.family_name") + " " + t("placeholder.given_name")}
            required
            {...form.getInputProps("full_name")}
          />
          <TextInput
            label={t("users.fields.login")}
            placeholder={t("placeholder.login")}
            required
            {...form.getInputProps("email")}
          />
          <Select
            label={t("users.fields.role")}
            placeholder={t("placeholder.select_role")}
            required
            searchable
            data={groupedOptions}
            {...form.getInputProps("effective_role")}
          />
          <PasswordInput
            label={t("users.fields.initial_password")}
            description={t("users.fields.password_hint")}
            placeholder="••••••••••••"
            required
            {...form.getInputProps("initial_password")}
          />
          <Group justify="flex-end" mt="sm">
            <Button variant="subtle" color="gray" onClick={onClose}>
              {t("common.cancel")}
            </Button>
            <Button type="submit" loading={mutation.isPending} radius="xl">
              {t("common.create")}
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
